import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Upload, RefreshCw, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

// Read-only spend import: a CSV (e.g. Google Ads "Day" report) or a Meta ads_read sync.
// Nothing here creates or changes ads.

const DATE_HEADERS = ['day', 'date', 'reporting starts', 'reporting start', 'start date'];
const COST_HEADERS = ['cost', 'amount spent', 'amount spent (gbp)', 'spend', 'amount', 'cost (gbp)'];

function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',' || ch === '\t') { out.push(cur.trim()); cur = ''; }
    else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function toIsoDate(value) {
  const v = (value || '').trim();
  let m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // UK day/month/year
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseSpendCsv(text, channel) {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim());
  let dateCol = -1;
  let costCol = -1;
  let start = -1;
  for (let i = 0; i < Math.min(lines.length, 15); i++) {
    const cells = parseCsvLine(lines[i]).map(c => c.toLowerCase());
    const d = cells.findIndex(c => DATE_HEADERS.includes(c));
    const c = cells.findIndex(x => COST_HEADERS.includes(x) || x.startsWith('cost') || x.startsWith('amount spent'));
    if (d >= 0 && c >= 0) { dateCol = d; costCol = c; start = i + 1; break; }
  }
  if (start < 0) return { rows: [], error: 'Couldn\'t find a date column (Day/Date) and a cost column (Cost/Amount spent).' };
  const rows = [];
  for (const line of lines.slice(start)) {
    const cells = parseCsvLine(line);
    const date = toIsoDate(cells[dateCol]);
    const amount = parseFloat((cells[costCol] || '').replace(/[£$€,\s]/g, ''));
    if (date && amount > 0) rows.push({ date, channel, amount });
  }
  return { rows, error: rows.length ? null : 'No spend rows found in that file.' };
}

export default function AdSpendImport({ canEdit, onChanged }) {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ ad_account_id: '', access_token: '' });
  const [busy, setBusy] = useState('');
  const [csvChannel, setCsvChannel] = useState('google');
  const fileRef = useRef(null);

  const loadMeta = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/training/ad-spend/meta`, { credentials: 'include' });
    if (res.ok) setMeta(await res.json());
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const call = async (key, path, method, body) => {
    setBusy(key);
    try {
      const res = await fetch(`${API_URL}/api/training/ad-spend/${path}`, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Something went wrong'); return null; }
      return data;
    } finally {
      setBusy('');
    }
  };

  const connect = async () => {
    const data = await call('connect', 'meta', 'PUT', form);
    if (data) {
      toast.success(`Meta connected — ${data.days_synced} days of spend synced`);
      if (data.currency && data.currency !== 'GBP') toast.warning(`This ad account reports in ${data.currency}, not GBP`);
      setForm({ ad_account_id: '', access_token: '' });
      loadMeta(); onChanged();
    }
  };

  const syncNow = async () => {
    const data = await call('sync', 'meta/sync', 'POST');
    if (data) { toast.success(`Synced ${data.days_synced} days from Meta`); loadMeta(); onChanged(); }
    else loadMeta();
  };

  const disconnect = async () => {
    if (await call('disconnect', 'meta', 'DELETE')) { toast.success('Meta disconnected (synced spend kept)'); loadMeta(); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (csvChannel === 'meta' && meta?.connected) { toast.error('Meta spend already syncs automatically, importing it too would count it twice'); return; }
    if (file.size > 2_000_000) { toast.error('That file is too big (max 2MB)'); return; }
    const { rows, error } = parseSpendCsv(await file.text(), csvChannel);
    if (error) { toast.error(error); return; }
    const data = await call('csv', 'import', 'POST', { rows: rows.slice(0, 2000) });
    if (data) {
      toast.success(`Imported: ${data.added} days added, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ''}`);
      onChanged();
    }
  };

  if (!canEdit) return null;

  return (
    <div className="space-y-3 border-t border-slate-100 pt-3 mt-2">
      <div className="rounded-xl border border-slate-200 p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-slate-800">Meta spend sync</p>
          {meta?.connected && <span className="text-xs text-emerald-600 font-medium">Connected · act_{meta.ad_account_id}</span>}
        </div>
        {meta?.connected ? (
          <>
            <p className="text-xs text-slate-500">
              Daily spend syncs automatically every 6 hours.
              {meta.last_sync_at ? ` Last sync ${new Date(meta.last_sync_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.` : ''}
            </p>
            {meta.last_error && <p className="text-xs text-red-600">Last sync failed: {meta.last_error}</p>}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-lg" onClick={syncNow} disabled={!!busy}>
                {busy === 'sync' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Sync now
              </Button>
              <Button size="sm" variant="ghost" className="rounded-lg text-slate-500" onClick={disconnect} disabled={!!busy}>
                <Unlink className="w-3.5 h-3.5 mr-1" /> Disconnect
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500">Read-only: we only read how much was spent each day. Use a token with just the <code>ads_read</code> permission.</p>
            <Input value={form.ad_account_id} onChange={(e) => setForm(p => ({ ...p, ad_account_id: e.target.value }))} placeholder="Ad account ID (e.g. 123456789012345)" className="rounded-lg h-9 text-sm" />
            <Input type="password" autoComplete="off" value={form.access_token} onChange={(e) => setForm(p => ({ ...p, access_token: e.target.value }))} placeholder="Access token (ads_read)" className="rounded-lg h-9 text-sm" />
            <Button size="sm" className="btn-primary rounded-lg w-full" onClick={connect} disabled={!!busy || !form.ad_account_id || !form.access_token}>
              {busy === 'connect' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1" />} Connect and sync last 30 days
            </Button>
          </>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 p-3 space-y-2">
        <p className="text-sm font-semibold text-slate-800">Import from a CSV</p>
        <p className="text-xs text-slate-500">
          Export a daily report (e.g. Google Ads → Reports → segment by Day, with the Cost column) and upload it. Re-importing the same days updates them instead of adding twice.
        </p>
        <div className="flex gap-2">
          <Select value={csvChannel} onValueChange={setCsvChannel}>
            <SelectTrigger className="rounded-lg h-9 w-32 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="google">Google</SelectItem>
              <SelectItem value="meta">Meta</SelectItem>
              <SelectItem value="tiktok">TikTok</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" accept=".csv,text/csv,.tsv" className="hidden" onChange={onFile} />
          <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => fileRef.current?.click()} disabled={!!busy}>
            {busy === 'csv' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Upload CSV
          </Button>
        </div>
      </div>
    </div>
  );
}
